/**
 * A driver's registration on his record (Pessoas › Motorista) (W9-DRIVER-REGISTRY, D481, ADR-0099; DevMobility D-012, A-009,
 * A-037, I-028, D-058): the consolidated readiness checklist, the registration data and the onboarding evidence.
 *
 * - **Checklist.** The same lines the driver's blocked app shows — account, the onboarding items the market requires, documents,
 *   vehicle, service — each with who must move (the driver, the operator, the platform) and the reason in words. The state
 *   (pronto, ação necessária, em análise, suspenso) is the server's.
 * - **Registration.** The map identifier, group, observation, birth date, address and the country's own fields. "Editar" opens a
 *   drawer that sends the changed form against the `revision` it was read at (a stale form is refused, not merged). A gender
 *   is shown only when it was recorded with its consent and is never edited here. Both need `driver:register`.
 * - **Evidence.** The newest line of the profile photo, the selfie and the e-mail check, with who recorded it; "Registrar" adds
 *   one line (a photo or selfie as a file, or an in-person check; a rejection says why). Lines are never edited.
 * - **Atua em** (D621). The lines of work the person is set up for (car or moto rides, food, parcels), the ride categories their
 *   inspected vehicles qualify for, their approved vehicles and whether the ONE decision clears them for deliveries now.
 * The server is the authority — the panel shows its answer. No native dialog is ever opened.
 */
import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { type DriverRegistryFormView, type DriverRegistryRecordView, type EvidenceLineView, type OnboardingEvidenceItemView, type DriverGroupView, type DocumentQueryView, type ReasonPartsView } from '@vello/api-client';
import { splitReason } from '@vello/driver-eligibility';
import { Badge, Button, Drawer, Field, FileInput, Grid, Notice, Select, TextInput, DrawerHeader, DrawerBody, DrawerFooter, Text, Stack, Strong, Ink, RecordPanel, RecordRow, RecordSection } from '@vello/web-theme';
import {
  READINESS_LINE_TONE,
  READINESS_STATE_TONE,
  checklistLines,
  driverRegistryAuthority,
  formatMarketInstant,
  marketTimeZone,
  packLabel,
  visibleCountryFields,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { documentTypeName } from '../i18n/document-type';
import { LoadingBlock } from './loading-block';
import { DrawerStack } from './drawer-stack';
import { RevealContact } from './reveal-contact';
import { PersonName } from './person-name';
import { WorkLineChips } from './work-lines';

const EVIDENCE_ITEMS: readonly OnboardingEvidenceItemView[] = ['PROFILE_PHOTO', 'SELFIE', 'VERIFIED_EMAIL'];
/** Photo and selfie travel as a file; the e-mail check is in person. */
const FILE_ITEMS: readonly OnboardingEvidenceItemView[] = ['PROFILE_PHOTO', 'SELFIE'];

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read'));
    reader.onload = () => resolve(typeof reader.result === 'string' ? (reader.result.split(',')[1] ?? '') : '');
    reader.readAsDataURL(file);
  });

interface EditDraft {
  readonly name: string;
  readonly email: string;
  readonly mapIdentifier: string;
  readonly observation: string;
  readonly birthDate: string;
  readonly groupId: string;
  readonly line1: string;
  readonly line2: string;
  readonly locality: string;
  readonly region: string;
  readonly postalCode: string;
  readonly attributes: Readonly<Record<string, string>>;
}

const draftOf = (r: DriverRegistryRecordView): EditDraft => ({
  name: r.account.name ?? '',
  email: r.account.email ?? '',
  mapIdentifier: r.profile?.mapIdentifier ?? '',
  observation: r.profile?.observation ?? '',
  birthDate: r.profile?.birthDate ?? '',
  groupId: r.group?.groupId ?? '',
  line1: r.profile?.address?.line1 ?? '',
  line2: r.profile?.address?.line2 ?? '',
  locality: r.profile?.address?.locality ?? '',
  region: r.profile?.address?.region ?? '',
  postalCode: r.profile?.address?.postalCode ?? '',
  attributes: r.profile?.attributes ?? {},
});

export function PersonRegistryPanel({ personId }: { personId: string }): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { toast } = useConsole();
  const { t, lang } = useT();
  const { v, explain } = useVocab();
  const { mayRegister } = driverRegistryAuthority(grants);
  const [record, setRecord] = useState<DriverRegistryRecordView | undefined>(undefined);
  const [form, setForm] = useState<DriverRegistryFormView | undefined>(undefined);
  const [groups, setGroups] = useState<readonly DriverGroupView[]>([]);
  const [docTypes, setDocTypes] = useState<DocumentQueryView['types'] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditDraft | undefined>(undefined);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [evItem, setEvItem] = useState<OnboardingEvidenceItemView>('PROFILE_PHOTO');
  const [evOutcome, setEvOutcome] = useState<'ACCEPTED' | 'REJECTED'>('ACCEPTED');
  const [evReason, setEvReason] = useState('');
  const [evNote, setEvNote] = useState('');
  const [evFile, setEvFile] = useState<File | undefined>(undefined);
  const [evError, setEvError] = useState<string | undefined>(undefined);

  const load = useCallback(async (): Promise<void> => {
    const res = await runtime.driverRegistry.record(personId);
    if (!res.ok) {
      setError(said(res.error));
      return;
    }
    setError(undefined);
    setRecord(res.value);
    const [f, g, d] = await Promise.all([runtime.driverRegistry.form(res.value.marketId), runtime.wallet.groups(res.value.marketId), runtime.driverRegistry.documents({ marketId: res.value.marketId, driverId: personId, limit: 1 })]);
    if (f.ok) setForm(f.value);
    if (d.ok) setDocTypes(d.value.types);
    if (g.ok) setGroups(g.value.groups.filter((x) => x.status === 'ACTIVE'));
  }, [runtime, personId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** A refusal in words: the catalog's sentence for its reason, else the plain words of its class. */
  const said = (e: { readonly code: string; readonly reasonCode?: string; readonly params?: Readonly<Record<string, string | number>> }): string => explain(e) ?? v('api.error_code', e.code);
  /** A stable readiness reason in words (an onboarding item and a document type are named in the operator's language). */
  const reasonText = (parts: ReasonPartsView): string => {
    const arg = parts.arg ?? '';
    const vars: Record<string, string> = parts.code.startsWith('ONBOARDING_') ? { item: v('driver.onboarding_item', arg) } : parts.code.startsWith('DOCUMENT_') ? { type: documentTypeName(docTypes, arg, lang, v) } : {};
    return v('driver.readiness_reason', parts.code, vars);
  };
  const tz = marketTimeZone(record?.marketId, markets);
  const when = (instant: string | undefined): string => formatMarketInstant(instant, tz, lang);

  const reasons = evOutcome === 'ACCEPTED' ? (form?.evidenceReasons.accept ?? []) : (form?.evidenceReasons.reject ?? []);
  const needsFile = FILE_ITEMS.includes(evItem) && evOutcome === 'ACCEPTED' && evItem === 'PROFILE_PHOTO';
  const mayUpload = FILE_ITEMS.includes(evItem) && evOutcome === 'ACCEPTED' && form?.fileUploads === true;
  const reasonValue = reasons.includes(evReason) ? evReason : (reasons[0] ?? '');
  const evidenceReady = reasonValue !== '' && (!needsFile || evFile !== undefined);

  const record1 = async (): Promise<void> => {
    if (!evidenceReady) return;
    setBusy(true);
    setEvError(undefined);
    let file: { contentType: string; bytesBase64: string } | undefined;
    if (mayUpload && evFile !== undefined) {
      try {
        file = { contentType: evFile.type, bytesBase64: await toBase64(evFile) };
      } catch {
        setBusy(false);
        setEvError(v('api.error_code', 'INTERNAL'));
        return;
      }
    }
    const res = await runtime.driverRegistry.recordEvidence(personId, {
      item: evItem,
      outcome: evOutcome,
      reasonCode: reasonValue,
      ...(evNote.trim() !== '' ? { note: evNote.trim() } : {}),
      ...(file ?? {}),
    });
    setBusy(false);
    if (!res.ok) {
      setEvError(said(res.error));
      return;
    }
    setEvFile(undefined);
    setEvNote('');
    toast({ message: t('registry.evidence.done'), icon: 'check', tone: 'success' });
    await load();
  };

  const openEdit = (): void => {
    if (record === undefined) return;
    setDraft(draftOf(record));
    setSaveError(undefined);
    setEditing(true);
  };

  const save = async (): Promise<void> => {
    if (record === undefined || draft === undefined) return;
    const was = draftOf(record);
    const patch: Record<string, unknown> = { revision: record.revision };
    const text = (k: 'name' | 'email' | 'mapIdentifier' | 'observation' | 'birthDate', nullable: boolean): void => {
      if (draft[k] !== was[k]) patch[k] = draft[k].trim() === '' && nullable ? null : draft[k].trim();
    };
    text('name', false);
    text('email', true);
    text('mapIdentifier', true);
    text('observation', true);
    text('birthDate', true);
    if (draft.groupId !== was.groupId) patch.groupId = draft.groupId === '' ? null : draft.groupId;
    const addressChanged = (['line1', 'line2', 'locality', 'region', 'postalCode'] as const).some((k) => draft[k] !== was[k]);
    if (addressChanged) {
      const filled = [draft.line1, draft.line2, draft.locality, draft.region, draft.postalCode].some((v) => v.trim() !== '');
      patch.address = !filled
        ? null
        : {
            line1: draft.line1.trim(), locality: draft.locality.trim(),
            ...(draft.line2.trim() !== '' ? { line2: draft.line2.trim() } : {}), ...(draft.region.trim() !== '' ? { region: draft.region.trim() } : {}), ...(draft.postalCode.trim() !== '' ? { postalCode: draft.postalCode.trim() } : {}),
          };
    }
    if (JSON.stringify(draft.attributes) !== JSON.stringify(was.attributes)) {
      patch.attributes = Object.fromEntries(Object.entries(draft.attributes).filter(([, v]) => v.trim() !== ''));
    }
    if (Object.keys(patch).length === 1) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setSaveError(undefined);
    const res = await runtime.driverRegistry.update(personId, patch as unknown as Parameters<typeof runtime.driverRegistry.update>[1]);
    setBusy(false);
    if (!res.ok) {
      setSaveError(said(res.error));
      return;
    }
    setEditing(false);
    toast({ message: t('registry.edit.done'), icon: 'check', tone: 'success' });
    setRecord(res.value.record);
  };

  const fields = form?.countryFields !== undefined && draft !== undefined ? visibleCountryFields(form.countryFields.fields, draft.attributes) : [];
  const latest = record?.evidence.latest ?? {};
  const recorder = (l: EvidenceLineView | undefined): ReactElement | null => (l === undefined ? null : <PersonName id={l.recordedBy} />);

  return (
    <RecordPanel
      title={t('registry.panel.title')}
      actions={record !== undefined ? <Badge tone={READINESS_STATE_TONE[record.readiness.state]}>{v('driver.readiness_state', record.readiness.state)}</Badge> : undefined}
      data-testid="person-registry"
    >
        {error !== undefined ? (
          <Text as="p" role="caption" tone="secondary">{error}</Text>
        ) : record === undefined ? (
          <LoadingBlock lines={2} />
        ) : (
          // D619 (owner 2026-10-18: the registration was "num bloco muito pequeno, meio perdido"): the panel spans the record's
          // width and its three parts sit side by side — the person's data first, then the readiness checklist, then the evidence.
          <Grid min="md" gap={6}>
            <RecordSection>
              <RecordRow>
                <Strong role="caption" tone="secondary">{t('registry.data.title')}</Strong>
                <span className="vello-grow" />
                {mayRegister && <Button variant="secondary" size="sm" onClick={openEdit}>{t('registry.data.edit')}</Button>}
              </RecordRow>
              {!record.registered && <Text as="p" role="caption" tone="secondary">{t('registry.data.legacy')}</Text>}
              {([
                ['registry.field.mapIdentifier', record.profile?.mapIdentifier],
                ['registry.field.group', record.group?.name],
                ['registry.field.birthDate', record.profile?.birthDate],
                ['registry.field.observation', record.profile?.observation],
                ['registry.field.email', record.account.email ?? record.account.emailMasked ?? undefined],
              ] as const).map(([k, v]) => (
                <RecordRow key={k} dense>
                  <Text role="caption" tone="secondary">{t(k)}</Text>
                  <Ink strong mono>{v ?? '—'}</Ink>
                </RecordRow>
              ))}
              <RecordRow dense>
                <Text role="caption" tone="secondary">{t('registry.field.phone')}</Text>
                <Ink strong mono><RevealContact userId={personId} last4={record.account.phoneLast4} /></Ink>
              </RecordRow>
              {record.profile?.address !== undefined && (
                <RecordRow dense>
                  <Text role="caption" tone="secondary">{t('registry.form.address')}</Text>
                  <Ink strong>
                    {[record.profile.address.line1, record.profile.address.line2, record.profile.address.locality, record.profile.address.region, record.profile.address.postalCode].filter((p) => p !== undefined && p !== '').join(', ')}
                  </Ink>
                </RecordRow>
              )}
              {record.countryFields !== undefined &&
                visibleCountryFields(record.countryFields.fields, record.profile?.attributes ?? {}).map((f) => {
                  const raw = record.profile?.attributes[f.key];
                  const label = raw !== undefined ? packLabel(f.options?.find((o) => o.value === raw)?.labels, lang, raw) : '—';
                  return (
                    <RecordRow key={f.key} dense>
                      <Text role="caption" tone="secondary">{packLabel(f.labels, lang, f.key)}</Text>
                      <Ink strong mono>{label}</Ink>
                    </RecordRow>
                  );
                })}
              {record.profile?.gender !== undefined && (
                <RecordRow dense>
                  <Text role="caption" tone="secondary">{t('registry.field.gender')}</Text>
                  <Ink strong>{v('driver.gender', record.profile.gender)}</Ink>
                  <Text role="caption" tone="secondary">{t('registry.data.genderConsent', { at: when(record.profile.genderConsentAt) })}</Text>
                </RecordRow>
              )}
            </RecordSection>

            <Stack gap={2} data-testid="registry-checklist">
              <Strong role="caption" tone="secondary">{t('registry.checklist.title')}</Strong>
              {checklistLines(record.readiness).map((line) => (
                <RecordRow key={line.key} data-testid={`registry-line-${line.key}`}>
                  <Badge tone={READINESS_LINE_TONE[line.status]}>{v('driver.readiness_line_status', line.status)}</Badge>
                  <Ink strong>{line.item !== undefined ? v('driver.onboarding_item', line.item) : v('driver.readiness_group', line.group)}</Ink>
                  {line.requirement === 'OPTIONAL' && <Text role="caption" tone="secondary">{t('registry.optional')}</Text>}
                  {line.owner !== undefined && line.status !== 'OK' && <Text role="caption" tone="secondary">{v('driver.readiness_owner', line.owner)}</Text>}
                  {(line.reasonParts ?? line.reasons.map(splitReason)).map((r) => (
                    <Text role="caption" tone="secondary" key={r.arg === undefined ? r.code : `${r.code}:${r.arg}`}>{reasonText(r)}</Text>
                  ))}
                </RecordRow>
              ))}
              {record.readiness.blockedReason !== undefined && (
                <Notice tone="warning">{t('registry.checklist.blocked', { reason: reasonText(record.readiness.blockedReasonParts ?? splitReason(record.readiness.blockedReason)) })}</Notice>
              )}
            </Stack>

            {record.work !== undefined && (
              <RecordSection data-testid="registry-work-lines">
                <Strong role="caption" tone="secondary">{t('worklines.record.title')}</Strong>
                <WorkLineChips lines={record.work.workLines} />
                <RecordRow dense>
                  <Text role="caption" tone="secondary">{t('worklines.record.categories')}</Text>
                  <Ink strong>{record.work.rideCategories.length > 0 ? record.work.rideCategories.map((c) => v('ride.category', c)).join(', ') : '—'}</Ink>
                </RecordRow>
                <RecordRow dense>
                  <Text role="caption" tone="secondary">{t('worklines.record.vehicles')}</Text>
                  <Ink strong>{record.work.vehicleClasses.length > 0 ? record.work.vehicleClasses.map((c) => v('service_categories.vehicle_class', c)).join(', ') : '—'}</Ink>
                </RecordRow>
                <RecordRow dense>
                  <Text role="caption" tone="secondary">{t('worklines.record.courier')}</Text>
                  <Badge tone={record.work.courierEligible ? 'success' : 'neutral'}>{t(record.work.courierEligible ? 'worklines.record.courier.ok' : 'worklines.record.courier.blocked')}</Badge>
                </RecordRow>
                <Text as="p" role="caption" tone="tertiary">{t('worklines.record.hint')}</Text>
              </RecordSection>
            )}

            <RecordSection data-testid="registry-evidence">
              <Strong role="caption" tone="secondary">{t('registry.evidence.title')}</Strong>
              {EVIDENCE_ITEMS.map((item) => {
                const l = latest[item];
                return (
                  <RecordRow key={item}>
                    <Ink strong>{v('driver.onboarding_item', item)}</Ink>
                    {l === undefined ? (
                      <Badge tone="neutral">{t('registry.evidence.none')}</Badge>
                    ) : (
                      <>
                        <Badge tone={l.outcome === 'ACCEPTED' ? 'success' : 'danger'}>{v('driver.onboarding_evidence_outcome', l.outcome)}</Badge>
                        <Text role="caption" tone="secondary">{v('driver.onboarding_evidence_reason', l.reasonCode)}</Text>
                        <Text role="caption" tone="secondary" mono>{when(l.recordedAt)}</Text>
                        <Text role="caption" tone="secondary" mono>{recorder(record.evidence.history.find((h) => h.item === item))}</Text>
                      </>
                    )}
                  </RecordRow>
                );
              })}
              {mayRegister && (
                <Stack gap={2}>
                  <Field label={t('registry.evidence.item')} htmlFor="reg-ev-item">
                    <Select id="reg-ev-item" value={evItem} onChange={(e) => { setEvItem(e.target.value as OnboardingEvidenceItemView); setEvFile(undefined); }}>
                      {EVIDENCE_ITEMS.map((i) => (
                        <option key={i} value={i}>{v('driver.onboarding_item', i)}</option>
                      ))}
                    </Select>
                  </Field>
                  <Field label={t('registry.evidence.outcome')} htmlFor="reg-ev-outcome">
                    <Select id="reg-ev-outcome" value={evOutcome} onChange={(e) => { setEvOutcome(e.target.value as 'ACCEPTED' | 'REJECTED'); setEvReason(''); }}>
                      <option value="ACCEPTED">{v('driver.onboarding_evidence_outcome', 'ACCEPTED')}</option>
                      <option value="REJECTED">{v('driver.onboarding_evidence_outcome', 'REJECTED')}</option>
                    </Select>
                  </Field>
                  <Field label={t('registry.evidence.reasonLabel')} htmlFor="reg-ev-reason">
                    <Select id="reg-ev-reason" value={reasonValue} onChange={(e) => setEvReason(e.target.value)}>
                      {reasons.map((r) => (
                        <option key={r} value={r}>{v('driver.onboarding_evidence_reason', r)}</option>
                      ))}
                    </Select>
                  </Field>
                  {mayUpload && (
                    <Field label={t(needsFile ? 'registry.evidence.file' : 'registry.evidence.fileOptional')} htmlFor="reg-ev-file" hint={t('registry.evidence.file.hint')}>
                      <FileInput
                        id="reg-ev-file"
                        accept="image/jpeg,image/png,image/webp"
                        value={evFile === undefined ? [] : [evFile]}
                        onChange={(files) => setEvFile(files[0])}
                        chooseLabel={t('registry.evidence.file.choose')}
                        replaceLabel={t('registry.evidence.file.replace')}
                        emptyLabel={t('registry.evidence.file.none')}
                        clearLabel={t('registry.evidence.file.clear')}
                      />
                    </Field>
                  )}
                  <Field label={t('registry.field.note')} htmlFor="reg-ev-note">
                    <TextInput id="reg-ev-note" value={evNote} maxLength={500} onChange={(e) => setEvNote(e.target.value)} />
                  </Field>
                  {evError !== undefined && <Notice tone="danger">{evError}</Notice>}
                  <div>
                    <Button variant="secondary" disabled={busy || !evidenceReady} onClick={() => void record1()}>{t('registry.evidence.record')}</Button>
                  </div>
                </Stack>
              )}
            </RecordSection>
          </Grid>
        )}

      <Drawer open={editing} onClose={() => setEditing(false)} labelledBy="registry-edit-title">
        <DrawerHeader titleId="registry-edit-title" title={t('registry.data.edit')} subtitle={t('registry.edit.hint')} />
        {draft !== undefined && (
          <DrawerBody>
            <DrawerStack>
              <Field label={t('registry.field.name')} htmlFor="rg-name"><TextInput id="rg-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></Field>
              <Field label={t('registry.field.email')} htmlFor="rg-email" hint={t('registry.edit.email.hint')}><TextInput id="rg-email" type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></Field>
              <Field label={t('registry.field.mapIdentifier')} htmlFor="rg-map"><TextInput id="rg-map" className="tnum" value={draft.mapIdentifier} onChange={(e) => setDraft({ ...draft, mapIdentifier: e.target.value })} /></Field>
              <Field label={t('registry.field.group')} htmlFor="rg-group">
                <Select id="rg-group" value={draft.groupId} onChange={(e) => setDraft({ ...draft, groupId: e.target.value })}>
                  <option value="">{t('registry.field.group.none')}</option>
                  {groups.map((g) => (
                    <option key={g.groupId} value={g.groupId}>{g.name}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t('registry.field.observation')} htmlFor="rg-obs"><TextInput id="rg-obs" value={draft.observation} maxLength={1000} onChange={(e) => setDraft({ ...draft, observation: e.target.value })} /></Field>
              <Field label={t('registry.field.birthDate')} htmlFor="rg-birth"><TextInput id="rg-birth" className="tnum" type="date" max={form?.today} value={draft.birthDate} onChange={(e) => setDraft({ ...draft, birthDate: e.target.value })} /></Field>
              <Field label={t('registry.field.line1')} htmlFor="rg-line1"><TextInput id="rg-line1" value={draft.line1} onChange={(e) => setDraft({ ...draft, line1: e.target.value })} /></Field>
              <Field label={t('registry.field.line2')} htmlFor="rg-line2"><TextInput id="rg-line2" value={draft.line2} onChange={(e) => setDraft({ ...draft, line2: e.target.value })} /></Field>
              <Field label={t('registry.field.locality')} htmlFor="rg-locality"><TextInput id="rg-locality" value={draft.locality} onChange={(e) => setDraft({ ...draft, locality: e.target.value })} /></Field>
              <Field label={t('registry.field.region')} htmlFor="rg-region"><TextInput id="rg-region" value={draft.region} onChange={(e) => setDraft({ ...draft, region: e.target.value })} /></Field>
              <Field label={t('registry.field.postalCode')} htmlFor="rg-postal"><TextInput id="rg-postal" className="tnum" value={draft.postalCode} onChange={(e) => setDraft({ ...draft, postalCode: e.target.value })} /></Field>
              {fields.map((f) => (
                <Field key={f.key} label={packLabel(f.labels, lang, f.key)} htmlFor={`rg-attr-${f.key}`} hint={f.hint !== undefined ? packLabel(f.hint, lang, '') : undefined}>
                  {f.input === 'CHOICE' ? (
                    <Select id={`rg-attr-${f.key}`} value={draft.attributes[f.key] ?? ''} onChange={(e) => setDraft({ ...draft, attributes: { ...draft.attributes, [f.key]: e.target.value } })}>
                      <option value="">{t('registry.choose')}</option>
                      {(f.options ?? []).map((o) => (
                        <option key={o.value} value={o.value}>{packLabel(o.labels, lang, o.value)}</option>
                      ))}
                    </Select>
                  ) : (
                    <TextInput id={`rg-attr-${f.key}`} className="tnum" value={draft.attributes[f.key] ?? ''} maxLength={f.maxLength} onChange={(e) => setDraft({ ...draft, attributes: { ...draft.attributes, [f.key]: e.target.value } })} />
                  )}
                </Field>
              ))}
              {saveError !== undefined && <Notice tone="danger">{saveError}</Notice>}
            </DrawerStack>
          </DrawerBody>
        )}
        <DrawerFooter>
          <Button variant="ghost" onClick={() => setEditing(false)}>{t('ui.cancel')}</Button>
          <span className="vello-grow" />
          <Button variant="primary" disabled={busy} onClick={() => void save()}>{t('registry.edit.save')}</Button>
        </DrawerFooter>
      </Drawer>
    </RecordPanel>
  );
}
